'use client';

import { c as _c } from "react/compiler-runtime";
import { reactive, useSelector } from '@legendapp/state/react';
import { $React } from '@legendapp/state/react-web';
import { type ReactNode, useId } from 'react';
import { useExpandableGridState } from '@/components/ExpandableGrid';
import { ReadMoreString } from '@/components/ReadMoreString';
import { Typography } from '@/components/Typography';
import { cn } from '@/lib/cn';
const ReactiveReadMoreString = reactive(ReadMoreString);
export type ExpandableGridCellProps = {
  heading?: string;
  description?: string;
  image?: ReactNode;
};
export function ExpandableGridCell(t0) {
  const $ = _c(24);
  const {
    heading,
    description,
    image
  } = t0;
  const id = useId();
  const expandableGridState$ = useExpandableGridState();
  let t1;
  if ($[0] !== expandableGridState$.active) {
    t1 = () => expandableGridState$.active.get();
    $[0] = expandableGridState$.active;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const active = useSelector(t1);
  let t2;
  if ($[2] !== expandableGridState$ || $[3] !== id) {
    t2 = () => {
      const expanded = expandableGridState$.isExpanded(id);
      const overlapped = expandableGridState$.isOverlapped(id);
      const isLast = expandableGridState$.isLast(id);
      return cn("rounded-2xl overflow-hidden border border-base-125 bg-base-150  transition-shadow inset-0 min-h-full flex", {
        "absolute bottom-auto": overlapped,
        "z-20": overlapped && isLast,
        "z-10": overlapped && !isLast,
        "shadow-lg": expanded,
        "shadow-none": !expanded
      });
    };
    $[2] = expandableGridState$;
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== heading) {
    t3 = heading ? <Typography as="div" variant="h6" className="title line-clamp-1">{heading}</Typography> : null;
    $[5] = heading;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== active || $[8] !== description || $[9] !== expandableGridState$ || $[10] !== id) {
    t4 = active ? <ReactiveReadMoreString lines={2} $readMoreClassName={() => cn("text-base-content/60 font-semibold no-underline", {
      "block text-right": expandableGridState$.isExpanded(id)
    })} text={description} onToggle={() => {
      expandableGridState$.toggleExpanded(id);
    }} $expanded={() => expandableGridState$.isExpanded(id)} /> : description;
    $[7] = active;
    $[8] = description;
    $[9] = expandableGridState$;
    $[10] = id;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <Typography as="div" variant="paragraph" className="description text-base-content/60 relative w-full">{t4}</Typography>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <div className="py-4 px-5 md:p-6 flex-grow-1">{t3}{t5}</div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== image || $[18] !== t2 || $[19] !== t6) {
    t7 = <$React.div $className={t2}>{image}{t6}</$React.div>;
    $[17] = image;
    $[18] = t2;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== id || $[22] !== t7) {
    t8 = <div key={id} className="relative expandable-grid-cell">{t7}</div>;
    $[21] = id;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}